import type { ComponentType } from 'react';

// ORDER IS LOAD-BEARING. `computeDirection` in atom.ts infers slide direction
// from each key's index here (earlier → later = forward, later → earlier =
// backward). Reordering these entries silently changes navigation animations,
// which is why scenes whose natural flow contradicts array position (e.g.
// login.tsx / signup-options.tsx) pass an explicit `direction` override to
// `goTo`. Keep this ordered by intended forward-flow, not alphabetically.
export const SCENE_KEYS = [
  // Download journeys (DEV-6513). Free: signup → usage; premium:
  // premium-gate → usage → signup → checkout redirect. The journeys traverse
  // signup/usage in opposite orders, so premium's usage → signup hop passes
  // an explicit forward `direction` override. `download.login` is the
  // returning-user alternative to signup, not a step in either journey.
  // `download.plan-choice` precedes signup in one arm of DEV-7278.
  'download.premium-gate',
  // Visitor premium download test (DEV-7217): the A/B arms' create-account
  // entry, in place of premium-gate. A then moves on to onboarding.plan-step.
  'download.premium-signup',
  'download.plan-choice',
  'download.signup',
  'download.login',
  'download.usage',
  'download.plan-picker',
  'download.ready',
  'auth.signup-options',
  'auth.signup-email',
  'auth.login',
  'auth.forgot-password',
  'auth.reset-password',
  'onboarding.auth-gate',
  'onboarding.plan-step',
  'onboarding.questionnaire',
  'onboarding.verify-email',
  // Partner application journey (DEV-6758): auth → application form →
  // success. `partner.not-eligible` replaces the form for existing partners
  // and server-rejected duplicates (DEV-6875).
  'partner.application',
  'partner.success',
  'partner.not-eligible',

  // Cancellation journey (DEV-6147). Entry step is manage-subscription, which
  // hands off to the pause-or-cancel decision (DEV-6181).
  'cancellation.manage-subscription',
  'cancellation.change-plan',
  'cancellation.confirm-plan-change',
  'cancellation.switch-to-yearly',
  'cancellation.switch-to-monthly',
  'cancellation.pause-or-cancel',
  'cancellation.pausing-means',
  'cancellation.pause-duration',
  'cancellation.pause-safelist-channels',
  'cancellation.pause-confirmation',
  'cancellation.pause-download-warning',
  'cancellation.resume',
  'cancellation.resume-confirmation',
  'cancellation.why-leave',
  'cancellation.offer',
  'cancellation.offer-claimed',
  'cancellation.remove-safelist',
  'cancellation.confirm-cancel',
  'cancellation.cancelled',
  'dev.scene-skeleton',
] as const;

export type SceneKey = (typeof SCENE_KEYS)[number];

export type Surface = 'modal' | 'fullscreen' | 'drawer';

export type ResponsiveSurface = {
  default: Exclude<Surface, 'drawer'>;
  mobile?: Surface;
};

export type SceneProps = {
  /** Close the stage entirely. */
  close: () => void;

  goTo: (key: SceneKey, opts?: { direction?: 'forward' | 'backward' }) => void;
};

export type SceneDefinition = {
  content: ComponentType<SceneProps>;
  surface: ResponsiveSurface;
  a11y: { title: string; description?: string };
  /**
   * Extra classes merged onto the stage's content surface, keyed per
   * surface so an override (e.g. a narrower modal max-width) can't leak
   * onto the drawer or fullscreen variants of the same scene.
   */
  surfaceClassName?: Partial<Record<Surface, string>>;
  /**
   * Hide the stage's close (X) on the mobile surface. For flows that provide
   * their own in-header back button as the exit affordance (e.g. the
   * cancellation journey), so mobile isn't left with two competing controls.
   */
  hideMobileClose?: boolean;
  /**
   * Extra classes merged onto the DialogScreens scroll container (the
   * element that owns `overflow-y-auto`). Use for scroll-behaviour tweaks
   * that must live on the scroller itself, e.g. `[scrollbar-gutter:stable]`.
   */
  screensClassName?: string;
};

/** Map of scene key → definition. Used by `satisfies` on the registry. */
export type SceneRegistry = Record<SceneKey, SceneDefinition>;
